/**
 * Mappers for reconciling UI types with Database types
 * 
 * These functions transform between:
 * - DbQuote (database schema - snake_case, nullable fields)
 * - Quote (UI schema - camelCase, enriched types)
 */

import type { Quote } from "@/data/quoteTypes";
import type { ProjectConfiguration } from "@/data/types";
import type { DbQuote, DbQuoteCreate, DbQuoteUpdate } from "@/types/database";
import { formatDisplayReference, type StageChange } from "@/data/opportunityTypes";
import { toUuidOrNull } from "@/lib/uuid";

function toJsonObject<T>(value: T | null | undefined): Record<string, unknown> | null {
  if (value == null) return null;
  return value as unknown as Record<string, unknown>;
}

function toJsonArray<T>(value: T[] | null | undefined): Record<string, unknown>[] | null {
  if (value == null) return null;
  return value as unknown as Record<string, unknown>[];
}

function toProjectConfig(raw: DbQuote["project_config"]): ProjectConfiguration | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  return raw as unknown as ProjectConfiguration;
}

function deriveProjectType(config: ProjectConfiguration | undefined): string {
  if (!config) return "Other";

  const fromConfig =
    config.autoProjectType ||
    config.projectCategory ||
    config.consultationCategory ||
    config.bathroomSystemType ||
    (config.selectedVerticals?.[0] ?? "");

  const leadContext = (config as unknown as { leadContext?: Record<string, unknown> }).leadContext;
  const fromLeadContext =
    leadContext && typeof leadContext.projectType === "string"
      ? leadContext.projectType
      : "";

  const resolved = (fromConfig || fromLeadContext || "").trim();
  return resolved || "Other";
}

export function parseAddressPartsFromText(address: string | null | undefined): { street: string; city: string; state: string; zip: string } {
  const raw = (address || "").trim();
  if (!raw) return { street: "", city: "", state: "", zip: "" };

  const parts = raw.split(",").map((part) => part.trim()).filter(Boolean);
  const street = parts[0] || raw;
  const city = parts.length > 1 ? parts[1] : "";
  let state = "";
  let zip = "";

  const stateZip = parts.length > 2 ? parts[2] : "";
  if (stateZip) {
    const m = stateZip.match(/^([A-Za-z]{2})\s*(\d{5}(?:-\d{4})?)?$/);
    if (m) {
      state = (m[1] || "").toUpperCase();
      zip = m[2] || "";
    } else {
      state = stateZip;
    }
  }

  return { street, city, state, zip };
}

function getLeadContextAddress(config: ProjectConfiguration | undefined): { street: string; city: string; state: string; zip: string } {
  const leadContext = (config as unknown as { leadContext?: Record<string, unknown> } | undefined)?.leadContext;
  if (!leadContext) return { street: "", city: "", state: "", zip: "" };

  const street = typeof leadContext.addressLine1 === "string"
    ? leadContext.addressLine1
    : (typeof leadContext.address === "string" ? leadContext.address : "");
  const city = typeof leadContext.city === "string" ? leadContext.city : "";
  const state = typeof leadContext.state === "string" ? leadContext.state : "";
  const zip = typeof leadContext.zip === "string" ? leadContext.zip : "";
  return { street: street.trim(), city: city.trim(), state: state.trim(), zip: zip.trim() };
}

function toQuoteScenarios(raw: DbQuote["scenarios"]): Quote["scenarios"] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw as unknown as Quote["scenarios"];
}

function toPricingGuardrails(raw: DbQuote["pricing_guardrails"]): Quote["pricingGuardrails"] | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  return raw as unknown as Quote["pricingGuardrails"];
}

function toPricingOverride(raw: DbQuote["pricing_override"]): Quote["pricingOverride"] | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  return raw as unknown as Quote["pricingOverride"];
}

function toVerification(raw: DbQuote["verification"]): Quote["verification"] | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  return raw as unknown as Quote["verification"];
}

function toContract(raw: DbQuote["contract"]): Quote["contract"] | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  return raw as unknown as Quote["contract"];
}

function toDeposit(raw: DbQuote["deposit"]): Quote["deposit"] | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  return raw as unknown as Quote["deposit"];
}

function toStageHistory(raw: DbQuote["stage_history"]): StageChange[] {
  if (!Array.isArray(raw)) return [];
  return raw as unknown as StageChange[];
}

/**
 * Transform a database quote to UI format
 * This is the same transformation that was inline in QuotesList.tsx
 */
export function toUiQuote(db: DbQuote): Quote {
  const projectConfig = toProjectConfig(db.project_config);
  const leadContextAddress = getLeadContextAddress(projectConfig);
  const parsedAddress = parseAddressPartsFromText(db.customer_address);
  const address = leadContextAddress.street || parsedAddress.street;
  const city = leadContextAddress.city || parsedAddress.city;
  const state = leadContextAddress.state || parsedAddress.state;
  const zip = leadContextAddress.zip || parsedAddress.zip;

  return {
    id: db.id,
    quoteId: db.id,
    leadId: db.lead_id || undefined,
    displayReference: formatDisplayReference(db.quote_number || db.quote_name || db.id),
    primaryName: db.customer_name || '',
    secondaryName: '',
    primaryPhone: db.customer_phone || '',
    secondaryPhone: '',
    primaryEmail: db.customer_email || '',
    secondaryEmail: '',
    address,
    city,
    state,
    zip,
    projectType: deriveProjectType(projectConfig),
    quoteName: db.quote_name || '',
    customerName: db.customer_name || '',
    customerEmail: db.customer_email || '',
    customerPhone: db.customer_phone || '',
    customerAddress: db.customer_address || '',
    leadSource: db.lead_source || undefined,
    stageHistory: toStageHistory(db.stage_history),
    ownershipHistory: [],
    activities: [],
    assignedRep: db.created_by || 'Unknown',
    appointmentId: db.appointment_id || undefined,
    status: db.status,
    stage: db.status,
    projectConfig,
    notes: db.notes || '',
    createdAt: db.created_at || '',
    updatedAt: db.updated_at || '',
    createdBy: db.created_by || 'Unknown',
    customerId: db.customer_id || undefined,
    organizationId: db.organization_id || undefined,
    convertedJobId: db.linked_job_id || undefined,
    linkedJobId: db.linked_job_id || undefined,
    totalEstimate: db.total_estimate || 0,
    scopeOfWork: db.scope_of_work || undefined,
    scenarios: toQuoteScenarios(db.scenarios),
    deposit: toDeposit(db.deposit),
    pricingGuardrails: toPricingGuardrails(db.pricing_guardrails),
    pricingOverride: toPricingOverride(db.pricing_override),
    pricingStatus: db.pricing_status as Quote["pricingStatus"],
    verification: toVerification(db.verification),
    contract: toContract(db.contract),
    lostReason: db.lost_reason || undefined,
    lockedAt: db.locked_at || undefined,
    lockedBy: db.locked_by || undefined,
    presentedAt: db.presented_at || undefined,
    wonAt: db.won_at || undefined,
    lostAt: db.lost_at || undefined,
  };
}

/**
 * Transform a UI quote to database format for create operations
 * Used when creating new quotes via QuoteService.create()
 */
export function toDbQuoteCreate(
  ui: Partial<Quote>, 
  createdBy?: string
): Omit<DbQuote, 'id' | 'created_at' | 'updated_at'> {
  const customerName = ui.primaryName || ui.customerName || '';
  const customerEmail = ui.primaryEmail || ui.customerEmail || null;
  const customerPhone = ui.primaryPhone || ui.customerPhone || null;
  const customerAddress = ui.address || ui.customerAddress || null;
  const actorId = toUuidOrNull(createdBy) ?? toUuidOrNull(ui.createdBy) ?? toUuidOrNull(ui.assignedRep);
  if (!actorId) {
    throw new Error("Quote create requires a valid UUID for created_by");
  }
  const appointmentId = toUuidOrNull(ui.appointmentId) ?? toUuidOrNull(ui.appointmentTime) ?? null;
  const stage = ui.stage || ui.status || 'Lead';

  return {
    quote_name: ui.quoteName || '',
    customer_name: customerName,
    customer_email: customerEmail,
    customer_phone: customerPhone,
    customer_address: customerAddress,
    lead_source: ui.leadSource || null,
    appointment_id: appointmentId,
    status: stage,
    project_config: toJsonObject(ui.projectConfig),
    notes: ui.notes || null,
    total_estimate: ui.totalEstimate || null,
    scope_of_work: ui.scopeOfWork || null,
    scenarios: toJsonArray(ui.scenarios),
    pricing_guardrails: toJsonObject(ui.pricingGuardrails),
    pricing_override: toJsonObject(ui.pricingOverride),
    pricing_status: ui.pricingStatus || null,
    verification: toJsonObject(ui.verification),
    deposit: toJsonObject(ui.deposit),
    lost_reason: ui.lostReason || null,
    locked_at: ui.lockedAt || null,
    locked_by: ui.lockedBy || null,
    presented_at: ui.presentedAt || null,
    won_at: ui.wonAt || null,
    lost_at: ui.lostAt || null,
    contract: toJsonObject(ui.contract),
    created_by: actorId,
    updated_by: actorId,
    lead_id: null,
    linked_job_id: ui.linkedJobId || null,
    branch_id: null, // Would be set based on user context
    stage_history: toJsonArray(ui.stageHistory),
  };
}

/**
 * Transform a UI quote to database format for update operations
 * Used when updating existing quotes via QuoteService.update()
 */
export function toDbQuoteUpdate(ui: Partial<Quote>): DbQuoteUpdate {
  const update: DbQuoteUpdate = {};
  
  if (ui.quoteName !== undefined) update.quote_name = ui.quoteName;
  if (ui.primaryName !== undefined || ui.customerName !== undefined) update.customer_name = ui.primaryName || ui.customerName || '';
  if (ui.primaryEmail !== undefined || ui.customerEmail !== undefined) update.customer_email = ui.primaryEmail || ui.customerEmail || null;
  if (ui.primaryPhone !== undefined || ui.customerPhone !== undefined) update.customer_phone = ui.primaryPhone || ui.customerPhone || null;
  if (ui.address !== undefined || ui.customerAddress !== undefined) update.customer_address = ui.address || ui.customerAddress || null;
  if (ui.leadSource !== undefined) update.lead_source = ui.leadSource || null;
  if (ui.appointmentTime !== undefined || ui.appointmentId !== undefined) {
    update.appointment_id = toUuidOrNull(ui.appointmentId) ?? toUuidOrNull(ui.appointmentTime) ?? null;
  }
  if (ui.stage !== undefined || ui.status !== undefined) update.status = ui.stage || ui.status || 'Lead';
  if (ui.projectConfig !== undefined) update.project_config = toJsonObject(ui.projectConfig);
  if (ui.notes !== undefined) update.notes = ui.notes || null;
  if (ui.totalEstimate !== undefined) update.total_estimate = ui.totalEstimate;
  if (ui.scopeOfWork !== undefined) update.scope_of_work = ui.scopeOfWork || null;
  if (ui.scenarios !== undefined) update.scenarios = toJsonArray(ui.scenarios);
  if (ui.pricingGuardrails !== undefined) update.pricing_guardrails = toJsonObject(ui.pricingGuardrails);
  if (ui.pricingOverride !== undefined) update.pricing_override = toJsonObject(ui.pricingOverride);
  if (ui.pricingStatus !== undefined) update.pricing_status = ui.pricingStatus || null;
  if (ui.verification !== undefined) update.verification = toJsonObject(ui.verification);
  if (ui.deposit !== undefined) update.deposit = toJsonObject(ui.deposit);
  if (ui.contract !== undefined) update.contract = toJsonObject(ui.contract);
  if (ui.lostReason !== undefined) update.lost_reason = ui.lostReason || null;
  if (ui.lockedAt !== undefined) update.locked_at = ui.lockedAt || null;
  if ((ui as Quote).lockedBy !== undefined) update.locked_by = (ui as Quote).lockedBy || null;
  if (ui.presentedAt !== undefined) update.presented_at = ui.presentedAt || null;
  if (ui.wonAt !== undefined) update.won_at = ui.wonAt || null;
  if (ui.lostAt !== undefined) update.lost_at = ui.lostAt || null;
  if (ui.linkedJobId !== undefined) {
    update.linked_job_id = ui.linkedJobId || null;
  } else if ((ui as any).convertedJobId !== undefined) {
    // Fallback for deprecated field name
    update.linked_job_id = (ui as any).convertedJobId || null;
  }
  if (ui.stageHistory !== undefined) update.stage_history = toJsonArray(ui.stageHistory);
  
  return update;
}

/**
 * Batch transform database quotes to UI format
 */
export function toUiQuotes(dbQuotes: DbQuote[]): Quote[] {
  return dbQuotes.map(toUiQuote);
}
